import React from 'react';
import { ImageIcon } from 'lucide-react';
import type { CategoryMapping } from '../../../data/categoryMapping';
import { Tag } from '../dash/ListRows';
import { cn } from '../../../lib/utils';

/** One category: thumbnail, editable display name, group, product-count bar, active toggle. */
export const CategoryRow: React.FC<{
  category: CategoryMapping;
  index: number;
  productCount: number;
  maxCount: number;
  groups: string[];
  editing: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onUpdate: (updates: Partial<CategoryMapping>) => void;
  onToggleActive: () => void;
}> = ({ category, index, productCount, maxCount, groups, editing, onStartEdit, onStopEdit, onUpdate, onToggleActive }) => {
  const share = maxCount > 0 ? Math.max(productCount / maxCount, productCount > 0 ? 0.03 : 0) : 0;

  return (
    // Columns come from .oa-cat-row in the theme CSS: oa-rowi's own two-column grid would beat a
    // Tailwind grid-cols-* here. The stagger is capped so row 100 does not wait six seconds.
    <div className={cn('oa-rowi oa-cat-row items-center gap-3 rounded-lg px-2 py-2', !category.isActive && 'opacity-50')} style={{ '--i': Math.min(index, 14) } as React.CSSProperties}>
      <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md border border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)]">
        {category.imageUrl ? (
          <img
            src={category.imageUrl}
            alt=""
            className="h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[color:var(--oa-faint)]">
            <ImageIcon className="h-4 w-4" />
          </div>
        )}
      </div>

      <div className="min-w-0">
        {editing ? (
          <input
            autoFocus
            value={category.displayName}
            onChange={(e) => onUpdate({ displayName: e.target.value })}
            onBlur={onStopEdit}
            onKeyDown={(e) => e.key === 'Enter' && onStopEdit()}
            className="h-7 w-full rounded-md border border-[color:var(--oa-accent)] bg-[color:var(--oa-card)] px-2 text-[13px] text-[color:var(--oa-text)] outline-none ring-2 ring-[color:color-mix(in_oklab,var(--oa-accent)_20%,transparent)]"
          />
        ) : (
          <button type="button" onClick={onStartEdit} className="block truncate text-left text-[13px] font-medium text-[color:var(--oa-text)] hover:underline">
            {category.displayName}
          </button>
        )}
        <span className="oa-sub block text-[color:var(--oa-faint)]">{category.name}</span>
      </div>

      <div className="oa-hide-phone">
        <select
          value={category.group}
          onChange={(e) => onUpdate({ group: e.target.value })}
          className="h-7 w-full rounded-md border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] px-1.5 text-[12px] text-[color:var(--oa-text)] outline-none focus:border-[color:var(--oa-accent)]"
        >
          {groups.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
      </div>

      <div className="min-w-0">
        <div className="oa-num text-[12.5px] text-[color:var(--oa-text)]">{productCount.toLocaleString()}</div>
        <div className="mt-1 h-1 overflow-hidden rounded-full bg-[color:var(--oa-raised)]">
          <i
            className="block h-full rounded-full"
            style={{
              width: `${Math.round(share * 100)}%`,
              background: productCount === 0 ? 'var(--oa-line-3)' : 'var(--oa-accent)',
              animation: 'oa-fillw 1.1s var(--oa-ease) both',
              animationDelay: `${400 + Math.min(index, 14) * 55}ms`,
            }}
          />
        </div>
      </div>

      <button type="button" onClick={onToggleActive}>
        <Tag color={category.isActive ? 'var(--oa-good)' : 'var(--oa-faint)'}>{category.isActive ? 'Active' : 'Inactive'}</Tag>
      </button>
    </div>
  );
};
